/**
 * WbModal — de ene schil voor alle dialogen.
 *
 * variant dialoog   vlak van onder, max-w-lg, ronde bovenhoeken (keuzes, bevestigen)
 * variant sheet     bijna volledig scherm van onder (composer, lange formulieren)
 * variant volledig  hele scherm zonder rand (scanner, camera-achtig)
 *
 * Eén sluitknop-positie (rechtsboven, 52 px), één knoppenrij onderin
 * (`acties`, 1 of 2 WbButtons, min-h-taplg), focus-trap, Escape, aria-modal,
 * scroll-lock op body en safe-area onderin. Rendert via een portal zodat een
 * getransformeerde ouder (stopkaart in animatie) de positionering niet
 * breekt. Dit is de plek waar iOS-eigenaardigheden één keer opgelost worden.
 *
 * Het smoke-script staat `fixed inset-0` alleen hier en in MediaLightbox toe.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

export interface WbModalProps {
  open: boolean;
  onClose: () => void;
  titel?: ReactNode;
  subtitel?: ReactNode;
  variant?: 'dialoog' | 'sheet' | 'volledig';
  /** Knoppenrij onderin; 1 of 2 WbButtons. */
  acties?: ReactNode;
  /** Sluitknop tonen en Escape/achtergrond laten sluiten (standaard aan). */
  sluitbaar?: boolean;
  /** Tik op de achtergrond sluit (standaard aan). Uit zodra een formulier ingevuld is: een mis-tik op een telefoon mag geen melding laten verdwijnen. */
  sluitOpAchtergrond?: boolean;
  children?: ReactNode;
  /** Extra classes op het vlak (bv. `p-0` voor een eigen layout). */
  className?: string;
  /** Voor de lightbox: boven een open modal. */
  boven?: boolean;
  'aria-label'?: string;
}

const FOCUSBAAR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let openTeller = 0;

export function WbModal({ open, onClose, titel, subtitel, variant = 'dialoog', acties, sluitbaar = true, sluitOpAchtergrond = true, children, className, boven = false, ...aria }: WbModalProps) {
  const vlakRef = useRef<HTMLDivElement>(null);
  const vorigeFocus = useRef<HTMLElement | null>(null);

  // Scroll-lock op body zolang er minstens één modal open is.
  useEffect(() => {
    if (!open) return;
    openTeller += 1;
    const vorige = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      openTeller -= 1;
      if (openTeller === 0) document.body.style.overflow = vorige;
    };
  }, [open]);

  // Focus naar binnen bij openen, terug bij sluiten; Tab blijft in het vlak; Escape sluit.
  useEffect(() => {
    if (!open) return;
    vorigeFocus.current = document.activeElement as HTMLElement | null;
    const vlak = vlakRef.current;
    const eerste = vlak?.querySelector<HTMLElement>('[data-wb-autofocus]') ?? vlak?.querySelector<HTMLElement>(FOCUSBAAR);
    (eerste ?? vlak)?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sluitbaar) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !vlak) return;
      const focusbaar = [...vlak.querySelectorAll<HTMLElement>(FOCUSBAAR)].filter((el) => el.offsetParent !== null);
      if (focusbaar.length === 0) { e.preventDefault(); return; }
      const eerste = focusbaar[0];
      const laatste = focusbaar[focusbaar.length - 1];
      if (!eerste || !laatste) return;
      if (e.shiftKey && document.activeElement === eerste) { e.preventDefault(); laatste.focus(); }
      else if (!e.shiftKey && document.activeElement === laatste) { e.preventDefault(); eerste.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      vorigeFocus.current?.focus?.({ preventScroll: true });
    };
  }, [open, sluitbaar, onClose]);

  if (!open) return null;

  const isVolledig = variant === 'volledig';
  const isSheet = variant === 'sheet';

  return createPortal(
    <div
      className={clsx('fixed inset-0 flex justify-center bg-black/60', isVolledig ? 'items-stretch' : 'items-end', boven ? 'z-wb-lightbox' : 'z-wb-modal')}
      onClick={sluitbaar && sluitOpAchtergrond && !isVolledig ? onClose : undefined}
    >
      <div
        ref={vlakRef}
        role="dialog"
        aria-modal="true"
        aria-label={aria['aria-label'] ?? (typeof titel === 'string' ? titel : undefined)}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          'wb-body relative w-full flex flex-col outline-none',
          isVolledig
            ? 'h-full'
            : isSheet
              ? 'max-w-lg h-[92dvh] rounded-t-wb-lg border-t-wb border-wb-rand'
              : 'max-w-lg max-h-[92dvh] rounded-t-wb-lg border-t-wb border-wb-rand',
          className
        )}
      >
        {(titel || sluitbaar) && (
          <div className={clsx('shrink-0 flex items-start gap-2 pl-4 pr-1 pt-2', !isVolledig && 'min-h-[56px]')}>
            <div className="flex-1 min-w-0 py-2">
              {titel && <h2 className="font-wb-heading font-bold text-lg leading-tight text-wb-inkt">{titel}</h2>}
              {subtitel && <p className="font-mono text-sm text-wb-inkt2 mt-0.5">{subtitel}</p>}
            </div>
            {sluitbaar && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Sluiten"
                className="wb-focus shrink-0 min-h-tap min-w-[52px] flex items-center justify-center text-wb-inkt rounded-wb"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className={clsx('flex-1 min-h-0 overflow-y-auto', !isVolledig && 'px-4 pb-3')}>{children}</div>

        {acties && (
          <div className="shrink-0 border-t-wb border-wb-rand bg-wb-vlak px-3 py-2.5 safe-area-bottom flex gap-2 [&>*]:flex-1">
            {acties}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
